import _ from "lodash";
import moment from "moment";
import React, { PropTypes } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Field,
  FieldArray,
  change as changeFieldValue,
  formValueSelector,
  reduxForm,
} from "redux-form";

import { FormattedMessage, intlShape } from "react-intl";
import * as dataActions from "../../../../../../../utils/data/dataActions";
import { Render } from "../../../../../../common";
import MessageBox from "../../../../../../common/MessageBox";
import {
  CheckboxInput,
  CurrencyInput,
  DayPicker,
  NewSelectInput,
  SelectInput,
  TextInput,
} from "../../../../../../common/form";

import validationUtils from "../../../../../../../utils/ValidationUtils";

import {
  BILLING_PERIODS,
  CLASSES,
  DIVISIONS,
} from "../../../../../../../utils/data/models";
import { MAX_DECIMAL } from "../../../../../../common/Configurations/DecimalConfiguration";
import { MANUAL } from "../../../../../clients/enums/billingPeriodTypes";
import { PM } from "../../../../enums/businessAccountTypes";
import { ACTIVE, INACTIVE } from "../../../../enums/clientStatusTypes";
import { BILLING_PERIOD } from "../../../../enums/ratePeriodTypes";
import RenderGuarantees from "./RenderGuarantees";
import {
  memberCoverageInactivityReasons,
  memberCoverageStatuses,
} from "./enums/";
import * as addTypes from "./enums/memberCoverageAddTypes";
import { ALL_INACTIVE_REASONS } from "./enums/memberCoverageInactivityReasons";
import { MEMBER_COVERAGE_VALIDATION_RULES } from "./memberCoverageValidationRules";
import { isMemberCoverageSynchronized } from "../../../../../../common/synchronization/SynchronizationHelper";
import { EXTERNAL_ROLES, HR_CLERK, PLAN_ADMINISTRATOR } from "../../../../../../../utils/Roles";
import userHasAccess from "../../../../../../../utils/userHasAccess";
import { MEMBER, MEMBER_MULTIPLIER } from "../../../../../admin/guarantees/enums/guaranteeTypes";

const CLASS_GUARANTEES = { ...CLASSES, name: "classGuarantees" };
const formName = "memberCoverage";

const generateDynamicRules = (props) => {
  const { isHighManagementWithManager, nextCoverage, previousCoverage, clientLastActiveDate } = props;
  let newRules = _.cloneDeep(MEMBER_COVERAGE_VALIDATION_RULES);
  if (clientLastActiveDate) {
    newRules.endDate.isRequiredTrim = {
      value: true
    };
  }
  if (isHighManagementWithManager) {
    if (nextCoverage) {
      newRules.endDate.lowerThanValue = {
        value: {
          value: moment(props.nextCoverage.startDate),
          customMessageKey:
            "memberCoverages.validation.endDateMustBeLowerThanNextCoverageStartDate",
        },
      };
      newRules.startDate.lowerThanValue = {
        value: {
          value: moment(props.nextCoverage.startDate),
          customMessageKey:
            "memberCoverages.validation.startDateMustBeLowerThanNextCoverageStartDate",
        },
      };
    }
    if (previousCoverage) {
      newRules.startDate.higherThanValue = {
        value: {
          value: moment(props.previousCoverage.startDate),
          customMessageKey:
            "memberCoverages.validation.startDateMustBeHigherThanPreviousCoverageStartDate",
        },
      };
    }
  }

  return newRules;
};

class BasicMemberCoverageFields extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      showInactivationError: false,
      isAfterClientLastActive: false,
      startDate: null,
      isBeyondRetroactiveLimit: false,
      initialCheck: false,
      disabledEndDate: false,
      hasSuccess: false,
      currentUserRoleId: localStorage.getItem("roleId") || "",
      latestLoadedClassId: null,
      hasClassChanged: false
    };
    this.compareGuarantees = this.compareGuarantees.bind(this);
    this.defineEndDate = this.defineEndDate.bind(this);
    this.onBillingPeriodChange = this.onBillingPeriodChange.bind(this);
    this.renderFieldArray = this.renderFieldArray.bind(this);
    this.checkChangeForRetroactiveLimit = this.checkChangeForRetroactiveLimit.bind(this);
    this.loadClassClientCoverages = this.loadClassClientCoverages.bind(this);
    this.parseClassId = this.parseClassId.bind(this);
    this.onClassChange = this.onClassChange.bind(this);

    this.hasChangedGuaranteesConfiguration = false;
  }

  getChildContext() {
    return {
      scene: this.props.scene,
    };
  }

  componentWillMount() {
    const {
      initialValues: { classId, billingPeriodId },
      clientId,
      clientBillingPeriodType,
      latestCoverage,
      readOnly,
      memberCoverageId
    } = this.props;

    this.onClassChange(classId);
    if (clientBillingPeriodType === MANUAL.value) {
      const fields = "billingPeriodId,endDate,startDate,localizations";
      let filters = [`billingPeriods.clientId==${clientId}`];
      if (!!latestCoverage && !readOnly) {
        filters.push(`billingPeriods.startDate>${latestCoverage.startDate}`);
      }
      if (readOnly) {
        filters.push(`billingPeriods.billingPeriodId==${billingPeriodId}`);
      }
      this.props.dataActions.getDataList(
        BILLING_PERIODS,
        0,
        0,
        "",
        ["startDate:desc"],
        filters,
        null,
        {},
        fields
      );
    }

    const divisionsFilters = [`clientId==${clientId}`];
    const divisionsSortBy = ["name:asc"];
    this.props.dataActions.getDataList(
      DIVISIONS,
      0,
      0,
      "",
      divisionsSortBy,
      divisionsFilters
    );
    const isPlanAdministratorOrHRClerk = [
      PLAN_ADMINISTRATOR.roleId.toString(),
      HR_CLERK.roleId.toString(),
    ].includes(this.state.currentUserRoleId);
    if (isPlanAdministratorOrHRClerk && memberCoverageId) {
      isMemberCoverageSynchronized(memberCoverageId, this.props.dataActions, (res) => {
        const hasSuccess = res && res.payload && res.payload.data && res.payload.data["isSynchronized"];
        this.setState({ hasSuccess });
      });
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.clientStatusType === INACTIVE.value) {
      const nextPropsFormattedDate = moment(
        nextProps.memberCoverageStartDate
      ).format("YYYY-MM-DD");

      if (!nextProps.add && nextProps.clientLastActiveDate) {
        this.setState({ startDate: nextPropsFormattedDate });
        if (
          nextProps.memberCoverageStartDate.isAfter(
            moment(nextProps.clientLastActiveDate)
          ) &&
          this.state.startDate === null
        ) {
          /**
           * If the coverage's start date is initially after the inactivation date, we block all modification.
           */
          this.setState({ isAfterClientLastActive: true });
        }
      }
    }

    const { classId } = this.props;
    const nextClassId = nextProps.classId;

    if (classId && nextClassId != classId) this.hasChangedGuaranteesConfiguration = true;
  }


  componentDidUpdate(prevProps) {
    if (this.props.fetchingRetroactiveDate === false && this.props.maximumRetroactiveDate) {
      if (this.props.initialValues.startDate && this.props.initialValues.startDate < moment(this.props.maximumRetroactiveDate, 'YYYY-MM-DD').toDate()
        && !this.state.initialCheck) {
        this.setState({
          isBeyondRetroactiveLimit: true,
          initialCheck: true
        });
        if ([ACTIVE.value, INACTIVE.value].includes(this.props.clientStatusType) && this.props.initialValues.endDate && this.props.initialValues.endDate < moment(this.props.maximumRetroactiveDate, 'YYYY-MM-DD').toDate()) {
          this.props.setBlockInactivationSubmission(true);
          this.setState({ disabledEndDate: true });
        }
      }
    }

    if (this.props.clientStatusType === INACTIVE.value) {
      const { memberCoverageStartDate, clientLastActiveDate } = this.props;
      if (prevProps.memberCoverageStartDate !== memberCoverageStartDate) {
        if (moment(memberCoverageStartDate).isAfter(clientLastActiveDate)) {
          this.props.setBlockInactivationSubmission(true);
          this.setState({ showInactivationError: true });
        } else if (!this.state.isAfterClientLastActive) {
          // We want to block any modification of retroactive coverages. We allow the removal of the error for add, because user can change the start date if he wants.
          this.props.setBlockInactivationSubmission(false);
          this.setState({ showInactivationError: false });
        }
      }
    }
  }

  componentWillUnmount() {
    this.props.setBlockInactivationSubmission(false);
    this.props.onStartDateChange(false, this.state.hasSuccess);
  }
  checkChangeForRetroactiveLimit(event) {
    const selectedDate = moment(event._d).format('YYYY-MM-DD');

    if (selectedDate && this.props.maximumRetroactiveDate && selectedDate >= this.props.maximumRetroactiveDate) {
      this.setState({
        isBeyondRetroactiveLimit: false
      });
      this.props.setBlockInactivationSubmission(false);
    }
    const startDate = this.props.initialValues.startDate ? moment(this.props.initialValues.startDate._d).format('YYYY-MM-DD') : null;
    this.props.onStartDateChange(false, this.state.hasSuccess);

    if (startDate && selectedDate != startDate) {
      this.props.onStartDateChange(true, this.state.hasSuccess);
    }
  }

  parseClassId(class_){
    if (class_ && typeof class_ == "object") {
      class_ = Object.values(class_)
        .filter((v) => typeof v == "string")
        .join("");
    }

    return class_;
  }

  loadClassClientCoverages(classId) {
    const orderBy = [
        "classClientCoverages.clientCoverage.guarantee.localizations.name:asc",
      ];
    const fields =
      "classClientCoverages(clientCoverage(clientCoverageId,commissionRate,ageOfPolicyDecrease,ageOfPolicyExpiration,guarantee))";

    this.props.dataActions.getDataList(
      { ...CLASS_GUARANTEES, path: CLASSES.path + "/" + classId },
      0,
      0,
      "",
      orderBy,
      [],
      null,
      {},
      fields
    );
  }

  onClassChange(value) {
    const classId = this.parseClassId(value);
    const isClassModified = classId != this.state.latestLoadedClassId;

    if (isClassModified) {
      if (!!classId) this.loadClassClientCoverages(classId);

      // At first load, class hasn't changed so we display guarantees check configuration
      // But then when the class is changed, we don't want to have a guarantee checked
      const hasClassChanged = this.state.latestLoadedClassId != null;
      
      this.setState({
        latestLoadedClassId: classId,
        hasClassChanged: hasClassChanged
      });
    }

    return classId;
  }

  allowanceBlur(commissionRate, target, event, newValue) {
    this.props.changeFieldValue(
      formName,
      target,
      (newValue - newValue * (commissionRate / 100)).toFixed(MAX_DECIMAL)
    );
  }

  safeAssign(target, source, keys) {
    keys.forEach(k => {
      if (source[k] != null) {
        target[k] = source[k];
      }
    });
    return target;
  }

  compareGuarantees(isExternalUser) {
    const { 
      initialValues, 
      guarantees,
      addType, 
      authLanguageCode, 
      memberCoverageGuarantees
    } = this.props;
    const memberGuarantees = this.hasChangedGuaranteesConfiguration ? memberCoverageGuarantees : initialValues.memberCoverageGuarantees;
    const memberHasCoverage = memberGuarantees.length > 0;

    return guarantees.map((guarantee) => {
      const guaranteeType = guarantee.clientCoverage.guarantee.guaranteeType;
      const isTypeMember = [MEMBER.value, MEMBER_MULTIPLIER.value].includes(guaranteeType);
      const isGuaranteeMustBeChecked = isTypeMember ? false : true;
      let match = false;
      
      if (memberHasCoverage) {
        match = memberGuarantees.find(
          (checkedGuarantee) =>
            checkedGuarantee.guarantee.guaranteeId ===
            guarantee.clientCoverage.guarantee.guaranteeId
        );
      }

      let field = {
        guaranteeId: guarantee.clientCoverage.guarantee.guaranteeId,
        clientCoverageId: guarantee.clientCoverage.clientCoverageId,
        name:
          guarantee.clientCoverage.guarantee.localizations[
            `${authLanguageCode}`
          ].name,
        placeHolderDecrease: guarantee.clientCoverage.ageOfPolicyDecrease,
        placeHolderExpiration: guarantee.clientCoverage.ageOfPolicyExpiration,
        isTypeMember: isTypeMember,
        guarantee: guarantee.clientCoverage.guarantee,
        commissionRate: guarantee.clientCoverage.commissionRate,
        coverageType: null,
        allowanceBlur: this.allowanceBlur.bind(
          this,
          guarantee.clientCoverage.commissionRate
        ),
      };

      if (addType === addTypes.NEW.value) 
        field.checked = memberHasCoverage ? match && match.checked || false : isGuaranteeMustBeChecked;
      else
        field.checked = this.hasChangedGuaranteesConfiguration ? !!match && match.checked || false : !!match;

      // We have to unsure the member type guarantees are the same as in database
      if (isExternalUser && isTypeMember) {
        if (!!match) 
          field.checked = 'checked' in match ? match.checked : !!match;
        else 
          field.checked = !!initialValues.memberCoverageGuarantees.find((g) => g.guarantee.guaranteeId == guarantee.clientCoverage.guarantee.guaranteeId); 
      }


      if (!!match) {
        field = this.safeAssign({ ...field }, match, [
          "ageOfPolicyDecrease",
          "ageOfPolicyExpiration",
          "coverageType",
          "allowance",
          "formulaMspa",
          "invoicedClientRate",
          "volume"
        ]);

        if (match.allowance != null) {
          field.supplierRate = (
            match.allowance - match.allowance * (guarantee.clientCoverage.commissionRate / 100)
          ).toFixed(MAX_DECIMAL);
        }
      }
      return field;
    });
  }

  defineEndDate(event) {
    const checked = event.target.checked;

    if (!checked) {
      this.props.changeFieldValue(formName, "endDate", null);
    }
  }

  onBillingPeriodChange(e) {
    const billingPeriodId = e.target.value;
    if (billingPeriodId !== " ") {
      const billingPeriod = this.props.billingPeriods.find(
        (period) => period.billingPeriodId === Number.parseInt(billingPeriodId)
      );
      this.props.changeFieldValue(
        formName,
        "startDate",
        billingPeriod.startDate
      );
      this.props.changeFieldValue(formName, "endDate", billingPeriod.endDate);
    } else {
      this.props.changeFieldValue(formName, "startDate", null);
      this.props.changeFieldValue(formName, "endDate", null);
    }
  }

  renderFieldArray(isBeyondRetroactiveLimitNotMEV, isExternalUser) {
    const { isHighManagementWithManager } = this.props;
    const guaranteesList = this.compareGuarantees(isExternalUser);
    const classSelected = this.props.classId && this.props.classId !== " ";
    const displayPerBillingPeriodRateMessage =
      this.props.ratePeriodType === BILLING_PERIOD.value &&
      guaranteesList.some((guarantee) => guarantee.isTypeMember);
    const isAfterClientLastActive = this.state.isAfterClientLastActive;
    return (
      <div className="sub-group">
        {displayPerBillingPeriodRateMessage && (
          <MessageBox className="alert-info">
            <FormattedMessage id="clientCoverages.form.warnings.billingPeriodRate" />
          </MessageBox>
        )}
        <h3>
          <FormattedMessage id="memberCoverages.form.guarantees" />
        </h3>
        <div className="field-array">
          <div className="fields">
            {classSelected ? (
              <FieldArray
                name="memberCoverageGuarantees"
                langCode={this.props.authLanguageCode}
                guarantees={guaranteesList}
                fetched={this.props.fetched}
                component={RenderGuarantees}
                initialValues={this.props.initialValues}
                formName={formName}
                classId={this.props.classId}
                readOnly={
                  (this.props.readOnly && !isHighManagementWithManager) ||
                  isAfterClientLastActive || isBeyondRetroactiveLimitNotMEV || this.props.disabled
                }
                add={this.props.add}
                ratePeriodType={this.props.ratePeriodType}
                firstCoverage={!this.props.latestCoverage}
                latestCoverage={this.props.latestCoverage}
                nextCoverage={this.props.nextCoverage}
                addType={this.props.addType}
              />
            ) : (
              <div>
                <FormattedMessage id="memberCoverages.form.noSelection" />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { isHighManagementWithManager, maximumRetroactiveDate } = this.props;
    const classesFilter = (_class) => {
      return (
        _class.classId === this.props.initialValues.classId ||
        _class.isArchived === false
      );
    };
    const classes = this.props.classesList
      .filter(classesFilter)
      .map((_class) => {
        return { value: _class.classId, text: _class.name };
      });
    const divisionsFilter = (division) => {
      return (
        division.divisionId === this.props.initialValues.divisionId ||
        division.isArchived === false
      );
    };
    const divisions = this.props.divisionList
      .filter(divisionsFilter)
      .map((division) => {
        return { value: division.divisionId, text: division.name };
      });
    const inactivationReasons =
      this.props.clientBillingPeriodType == MANUAL.value &&
        this.props.businessAccountType == PM.value
        ? ALL_INACTIVE_REASONS
        : memberCoverageInactivityReasons;
    const missingFieldsAlert = this.props.missingProfileFields.length ? (
      <MessageBox className="alert-danger">
        <div>
          <FormattedMessage id="memberCoverages.errors.messages.missing_required_fields" />
          <ul>
            {this.props.missingProfileFields.map((field) => (
              <li key={field}>
                <FormattedMessage
                  id={`memberCoverages.errors.messages.fields.${field}`}
                />
              </li>
            ))}
          </ul>
        </div>
      </MessageBox>
    ) : null;
    const maxDate = this.props.clientLastActiveDate ? moment(this.props.clientLastActiveDate) : undefined;
    const potentialInactivationErrKey = this.props.add
      ? "memberCoverages.errors.messages.newMemberCoverageInactivationError"
      : "memberCoverages.errors.messages.modifyMemberCoverageInactivationError";
    const isAfterClientLastActive = this.state.isAfterClientLastActive;
    const isEndDateRequired = maxDate ? true : false;
    const isBeyondRetroactiveLimitNotMEV = this.state.isBeyondRetroactiveLimit && [ACTIVE.value, INACTIVE.value].includes(this.props.clientStatusType) || this.props.fetchingRetroactiveDate;
    const isExternalUser = userHasAccess(EXTERNAL_ROLES, true);

    return (
      <form onSubmit={this.props.handleSubmit}>
        <fieldset disabled={this.props.disabled}>
          <div className="sub-group">
            {missingFieldsAlert}
            <Render if={this.props.clientBillingPeriodType === MANUAL.value}>
              <Field
                id="billingPeriodId"
                name="billingPeriodId"
                component={SelectInput}
                props={{ onChange: this.onBillingPeriodChange }}
                label={
                  <FormattedMessage id="memberCoverages.form.billingPeriod" />
                }
                addBlankOption={true}
                sortOptions={false}
                options={this.props.billingPeriods.map((period) => {
                  return {
                    value: period.billingPeriodId,
                    text:
                      period.localizations[this.props.authLanguageCode].name,
                  };
                })}
                disabled={
                  ((this.props.readOnly || this.props.disabled) &&
                    !isHighManagementWithManager) ||
                  isAfterClientLastActive
                }
              />
            </Render>
            <Render if={this.props.clientBillingPeriodType !== MANUAL.value}>
              {!isAfterClientLastActive && (
                <MessageBox key="startDateWarning" className="alert-warning">
                  {maximumRetroactiveDate ?
                    <FormattedMessage id="memberCoverages.form.warningDateForRecurring" values={{ date: maximumRetroactiveDate }} />
                    :
                    <FormattedMessage id="memberCoverages.form.warningRetroModification" />}
                </MessageBox>
              )}
              {this.state.showInactivationError && (
                <MessageBox className="alert-danger">
                  <FormattedMessage id={potentialInactivationErrKey} />
                </MessageBox>
              )}
              <Field
                id="startDate"
                name="startDate"
                component={DayPicker}
                required={true}
                minDate={maximumRetroactiveDate}
                maxDate={maxDate}
                onChange={this.checkChangeForRetroactiveLimit}
                showClearButton={true}
                label={<FormattedMessage id="memberCoverages.form.startDate" />}
                disabled={
                  (this.props.readOnly && !isHighManagementWithManager) ||
                  isAfterClientLastActive || isBeyondRetroactiveLimitNotMEV}
              />
              {maxDate && (
                <MessageBox className="alert-warning">
                  <FormattedMessage id="pastLastActiveDateError" />
                </MessageBox>
              )}
            </Render>
            <Render
              if={
                !this.props.readOnly &&
                !this.props.disabled &&
                this.props.clientBillingPeriodType === "RECURRING"
              }
            >
              <Field
                props={{ onChange: this.defineEndDate }}
                id="defineEndDate"
                name="defineEndDate"
                component={CheckboxInput}
                checkbox={{ value: true }}
                checked={isEndDateRequired}
                required={isEndDateRequired}
                label={
                  <FormattedMessage id="memberCoverages.form.defineEndDate" />
                }
                disabled={this.props.readOnly || isAfterClientLastActive || isEndDateRequired || this.props.fetchingRetroactiveDate}
                normalize={(value) => !!value}
              />
            </Render>
            <Render
              if={
                this.props.clientBillingPeriodType === "RECURRING" &&
                (this.props.defineEndDate || isEndDateRequired)
              }
            >
              <Field
                id="endDate"
                name="endDate"
                component={DayPicker}
                required={true}
                minDate={maximumRetroactiveDate}
                maxDate={maxDate}
                showClearButton={true}
                label={<FormattedMessage id="memberCoverages.form.endDate" />}
                disabled={
                  (this.props.readOnly && !isHighManagementWithManager) ||
                  isAfterClientLastActive || this.state.disabledEndDate || this.props.fetchingRetroactiveDate
                }
              />
            </Render>
            <Field
              id="isInactive"
              name="isInactive"
              component={NewSelectInput}
              disabled={
                ((this.props.add &&
                    ![
                      addTypes.STATUS.value,
                      addTypes.OTHER.value,
                      addTypes.NEW.value,
                    ].includes(this.props.addType))) &&
                !isHighManagementWithManager || isBeyondRetroactiveLimitNotMEV
                || this.props.disabled
              }
              options={memberCoverageStatuses}
              label={<FormattedMessage id="memberCoverages.form.status" />}
            />
            <Render
              if={
                this.props.isInactive &&
                this.props.businessAccountType == PM.value
              }
            >
              <span>
                <Field
                  id="isInactiveReasonType"
                  name="isInactiveReasonType"
                  component={SelectInput}
                  required={true}
                  disabled={
                    (this.props.disabled && !isHighManagementWithManager) ||
                    isAfterClientLastActive || isBeyondRetroactiveLimitNotMEV
                  }
                  options={inactivationReasons}
                  addBlankOption={true}
                  sortOptions={false}
                  label={
                    <FormattedMessage id="memberCoverages.form.inactivityReason" />
                  }
                  normalize={this.changeReason}
                />
                <Render if={this.props.inactivityReason === "OTHER"}>
                  <Field
                    id="isInactiveOtherReason"
                    name="isInactiveOtherReason"
                    component={TextInput}
                    required={true}
                    type="text"
                    label={
                      <FormattedMessage id="memberCoverages.form.inactivityReasonDetails" />
                    }
                    disabled={isAfterClientLastActive || isBeyondRetroactiveLimitNotMEV}
                  />
                </Render>
                <Render
                  if={
                    this.props.inactivityReason &&
                    this.props.inactivityReason.includes("DEACTIVATION")
                  }
                >
                  <Field
                    id="isInactiveWithdrawalDate"
                    name="isInactiveWithdrawalDate"
                    component={DayPicker}
                    required={true}
                    showClearButton={true}
                    useCurrent={false}
                    label={
                      <FormattedMessage id="memberCoverages.form.inactivityReasonDate" />
                    }
                    normalize={this.changeDate}
                    disabled={isAfterClientLastActive || isBeyondRetroactiveLimitNotMEV}
                  />
                </Render>
              </span>
            </Render>

            <Render if={this.props.clientBillingPeriodType === MANUAL.value}>
              <Field
                disabled={
                  (this.props.add &&
                    ![addTypes.OTHER.value, addTypes.NEW.value].includes(
                      this.props.addType
                    ) &&
                    !isHighManagementWithManager) ||
                  isAfterClientLastActive
                }
                id="mustBePaidToBroker"
                name="mustBePaidToBroker"
                component={CheckboxInput}
                type="checkbox"
                checkbox={{ value: true }}
                label={
                  <FormattedMessage id="memberCoverages.form.paidToBroker" />
                }
                normalize={(value) => !!value}
              />
              <Field
                disabled={
                  (this.props.add &&
                    ![addTypes.OTHER.value, addTypes.NEW.value].includes(
                      this.props.addType
                    ) &&
                    !isHighManagementWithManager) ||
                  isAfterClientLastActive || isBeyondRetroactiveLimitNotMEV
                }
                id="isPaid"
                name="isPaid"
                component={CheckboxInput}
                type="checkbox"
                checkbox={{ value: true }}
                label={<FormattedMessage id="memberCoverages.form.paid" />}
                normalize={(value) => !!value}
              />
              <Field
                disabled={
                  ((this.props.readOnly ||
                    (this.props.add &&
                      ![addTypes.OTHER.value, addTypes.NEW.value].includes(
                        this.props.addType
                      ))) &&
                    !isHighManagementWithManager) ||
                  isAfterClientLastActive || isBeyondRetroactiveLimitNotMEV
                }
                id="isRemovedFromList"
                name="isRemovedFromList"
                component={CheckboxInput}
                type="checkbox"
                checkbox={{ value: true }}
                label={
                  <FormattedMessage id="memberCoverages.form.removeFromList" />
                }
                normalize={(value) => !!value}
              />
            </Render>

            <Render if={!isExternalUser}>
              <Field
                id="mustNotInvoice"
                name="mustNotInvoice"
                component={CheckboxInput}
                type="checkbox"
                checkbox={{ value: true }}
                label={
                  <FormattedMessage id="memberCoverages.form.mustNotInvoice" />
                }
                disabled={
                  ((this.props.readOnly ||
                    (this.props.add &&
                      ![addTypes.OTHER.value, addTypes.NEW.value].includes(
                        this.props.addType
                      ))) &&
                    !isHighManagementWithManager) ||
                  isAfterClientLastActive || isBeyondRetroactiveLimitNotMEV
                }
                normalize={(value) => !!value}
              />
            </Render>

            <Field
              id="classId"
              name="classId"
              component={SelectInput}
              required={true}
              options={classes}
              label={<FormattedMessage id="memberCoverages.form.class" />}
              onChange={this.onClassChange}
              disabled={
                ((this.props.readOnly ||
                  (this.props.add &&
                    ![
                      addTypes.CLASS.value,
                      addTypes.OTHER.value,
                      addTypes.NEW.value,
                    ].includes(this.props.addType))) &&
                  !isHighManagementWithManager) ||
                isAfterClientLastActive || isBeyondRetroactiveLimitNotMEV || this.props.disabled
              }
            />

            <Field
              id="divisionId"
              name="divisionId"
              component={SelectInput}
              required={true}
              options={divisions}
              label={<FormattedMessage id="members.form.division" />}
              disabled={
                ((this.props.readOnly ||
                  (this.props.add &&
                    ![
                      addTypes.DIVISION.value,
                      addTypes.OTHER.value,
                      addTypes.NEW.value,
                    ].includes(this.props.addType))) &&
                  !isHighManagementWithManager) ||
                isAfterClientLastActive || isBeyondRetroactiveLimitNotMEV || this.props.disabled
              }
            />

            <Field
              id="occupation"
              name="occupation"
              component={TextInput}
              type="text"
              label={<FormattedMessage id="memberCoverages.form.occupation" />}
              disabled={
                ((this.props.readOnly ||
                  (this.props.add &&
                    ![addTypes.OTHER.value, addTypes.NEW.value].includes(
                      this.props.addType
                    ))) &&
                  !isHighManagementWithManager) ||
                isAfterClientLastActive || isBeyondRetroactiveLimitNotMEV
              }
            />

            <Render if={!userHasAccess(HR_CLERK, true)}>
              <Field
                id="salary"
                name="salary"
                component={CurrencyInput}
                required={true}
                label={<FormattedMessage id="memberCoverages.form.salary" />}
                disabled={
                  ((this.props.readOnly ||
                    (this.props.add &&
                      ![
                        addTypes.SALARY.value,
                        addTypes.OTHER.value,
                        addTypes.NEW.value,
                      ].includes(this.props.addType))) &&
                    !isHighManagementWithManager) ||
                  isAfterClientLastActive || isBeyondRetroactiveLimitNotMEV
                }
              />
            </Render>

            <Field
              id="arePremiumsWaived"
              name="arePremiumsWaived"
              component={CheckboxInput}
              type="checkbox"
              checkbox={{ value: true }}
              label={
                <FormattedMessage id="memberCoverages.form.arePremiumsWaived" />
              }
              disabled={
                ((this.props.readOnly ||
                  (this.props.add &&
                    ![addTypes.OTHER.value, addTypes.NEW.value].includes(
                      this.props.addType
                    ))) &&
                  !isHighManagementWithManager) ||
                isAfterClientLastActive || isBeyondRetroactiveLimitNotMEV
              }
              normalize={(value) => !!value}
            />
          </div>

          {this.renderFieldArray(isBeyondRetroactiveLimitNotMEV, isExternalUser)}
        </fieldset>
      </form >
    );
  }
}

BasicMemberCoverageFields.propTypes = {
  add: PropTypes.bool,
  addType: PropTypes.string,
  billingPeriods: PropTypes.array,
  changeFieldValue: PropTypes.func,
  classId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  classesList: PropTypes.array,
  clientBillingPeriodType: PropTypes.string,
  businessAccountType: PropTypes.number,
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dataActions: PropTypes.object,
  defineEndDate: PropTypes.bool,
  disabled: PropTypes.bool,
  divisionList: PropTypes.array,
  fetched: PropTypes.bool,
  handleSubmit: PropTypes.func,
  inactivityReason: PropTypes.string,
  initialValues: PropTypes.object,
  isInactive: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  latestCoverage: PropTypes.object,
  nextCoverage: PropTypes.object,
  previousCoverage: PropTypes.object,
  ratePeriodType: PropTypes.string,
  readOnly: PropTypes.bool,
  scene: PropTypes.string,
  validation: PropTypes.object,
  guarantees: PropTypes.array,
  authLanguageCode: PropTypes.string,
  memberCoverageType: PropTypes.string,
  clientInactivationError: PropTypes.string,
  setBlockInactivationSubmission: PropTypes.func,
  clientLastActiveDate: PropTypes.string,
  clientStatusType: PropTypes.string,
  memberCoverageStartDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  missingProfileFields: PropTypes.array,
  memberCoverageGuarantees: PropTypes.array,
  maximumRetroactiveDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  isHighManagementWithManager: PropTypes.bool,
  fetchingRetroactiveDate: PropTypes.bool,
  memberCoverageId: PropTypes.number,
  onStartDateChange: PropTypes.func,
};

BasicMemberCoverageFields.defaultProps = {
  disabled: false,
  scene: "",
  missingProfileFields: [],
};

BasicMemberCoverageFields.contextTypes = {
  intl: intlShape,
};

BasicMemberCoverageFields.childContextTypes = {
  scene: PropTypes.string,
};

function mapStateToProps(state) {
  const data = state.data.classGuarantees || {};
  const billingPeriods = state.data.billingPeriods || {};
  const divisionData = state.data.divisions || {};
  const activeClient = state.data.clients.active;

  const selector = formValueSelector(formName);

  return {
    authLanguageCode: state.auth.user.languageCode,
    billingPeriods: billingPeriods.items || [],
    classId: selector(state, "classId"),
    defineEndDate: selector(state, "defineEndDate"),
    divisionList: divisionData.items || [],
    fetched: data.fetched,
    guarantees: data.classClientCoverages || [],
    inactivityReason: selector(state, "isInactiveReasonType"),
    isInactive: selector(state, "isInactive"),
    memberCoverageGuarantees: selector(state, "memberCoverageGuarantees"),
    memberCoverageStartDate: selector(state, "startDate"),
    clientStatusType: activeClient.clientStatusType,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    changeFieldValue: bindActionCreators(changeFieldValue, dispatch),
    dataActions: bindActionCreators(dataActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  withRef: true,
})(
  reduxForm({
    form: formName,
    validate: (values, props) =>
      validationUtils.validate(values, generateDynamicRules(props)),
  })(BasicMemberCoverageFields)
);



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/members/member/coverage/BasicMemberCoverageFields.js